import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Calendar, Tag, ArrowLeft, User, Eye } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import type { ChangelogEntry } from '../types/changelog'
import { useTenant } from '../contexts/TenantContext'

const ChangelogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  const [entry, setEntry] = useState<ChangelogEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (slug) {
      fetchEntry(slug)
    }
  }, [slug, currentTenant])

  const fetchEntry = async (entrySlug: string) => {
    try {
      setLoading(true)
      setError('')
      const response = await fetch(`/api/changelog/${entrySlug}`)

      if (response.ok) {
        const data = await response.json()
        setEntry(data)
      } else if (response.status === 404) {
        setError('Changelog entry not found')
      } else {
        setError('Failed to load changelog entry')
      }
    } catch (err) {
      console.error('Error fetching changelog entry:', err)
      setError('Failed to load changelog entry')
    } finally {
      setLoading(false)
    }
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      feature: 'bg-blue-100 text-blue-800 border-blue-200',
      improvement: 'bg-green-100 text-green-800 border-green-200',
      fix: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      security: 'bg-red-100 text-red-800 border-red-200',
      announcement: 'bg-purple-100 text-purple-800 border-purple-200'
    }
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1)
  }

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return ''
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <button
              onClick={() => navigate('/changelog')}
              className="flex items-center text-primary-100 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Changelog
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading entry...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !entry) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <button
              onClick={() => navigate('/changelog')}
              className="flex items-center text-primary-100 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Changelog
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg">{error || 'Entry not found'}</p>
            <button
              onClick={() => navigate('/changelog')}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Return to Changelog
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <button
            onClick={() => navigate('/changelog')}
            className="flex items-center text-primary-100 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Changelog
          </button>

          {/* Entry Title */}
          <h1 className="text-4xl font-bold mb-4">{entry.title}</h1>

          {/* Description */}
          {entry.description && (
            <p className="text-xl text-primary-100 mb-6">
              {entry.description}
            </p>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center text-primary-100">
              <Calendar className="h-4 w-4 mr-2" />
              {formatDate(entry.publishedAt || entry.createdAt)}
            </div>

            <span className={`px-3 py-1 text-sm font-medium rounded border ${getCategoryColor(entry.category)}`}>
              {getCategoryLabel(entry.category)}
            </span>

            {(entry.severity === 'critical' || entry.severity === 'high') && (
              <span className="px-3 py-1 text-sm font-medium rounded border bg-red-100 text-red-800 border-red-200">
                {entry.severity.toUpperCase()}
              </span>
            )}

            {entry.featured && (
              <span className="px-3 py-1 text-sm font-medium rounded border bg-yellow-100 text-yellow-800 border-yellow-200">
                Featured
              </span>
            )}

            {entry.authorName && (
              <div className="flex items-center text-primary-100">
                <User className="h-4 w-4 mr-2" />
                {entry.authorName}
              </div>
            )}

            {entry.viewCount > 0 && (
              <div className="flex items-center text-primary-100">
                <Eye className="h-4 w-4 mr-2" />
                {entry.viewCount} {entry.viewCount === 1 ? 'view' : 'views'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <article className="prose prose-lg max-w-none p-8">
            <ReactMarkdown>{entry.content}</ReactMarkdown>
          </article>

          {/* Tags Section */}
          {entry.tags.length > 0 && (
            <div className="px-8 pb-8">
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500 font-medium">Tags:</span>
                  {entry.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 cursor-pointer"
                      onClick={() => navigate(`/changelog?tag=${encodeURIComponent(tag)}`)}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* GitHub Link */}
          {entry.githubData && entry.githubData.url && (
            <div className="px-8 pb-8">
              <div className="border-t border-gray-200 pt-6">
                <a
                  href={entry.githubData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                >
                  View on GitHub
                  <svg className="ml-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/changelog')}
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Updates
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChangelogDetailPage
